// ManualPayouts — the rail for banks with no API. SUPERADMIN ONLY.
//
// Route: /manual-payouts. Two tabs behind ONE nav entry, deliberately: the
// sidebar is already long, and these two things are halves of one job — you
// define the layout a bank wants, then produce files in it.
//
//   Payment files — claim the waiting payouts into a CSV, download it, upload
//                   it to the bank, come back and record what happened.
//   CSV layouts   — define the columns that bank's template expects.
//
// The one thing this screen must never do is let the same payout end up in two
// files. That is enforced in the database (payouts.export_id is stamped inside
// the transaction that creates the export), not here — but the UI is written
// to match: "Generate file" is a single deliberate action with a confirmation
// showing exactly what it will claim, and downloading is separate from
// claiming so re-downloading can never take more money.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { EmptyState, Pill, Segmented } from "@/components/UI";
import { useAuth, isSuperadminRole, effectiveRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import { apiErrorMessage } from "@/utils/apiError";
import { fmtCcyMinorExact } from "@/utils/format";
import { saveBlob } from "@/utils/csv";
import { listAdminBankRails } from "@/api/admin";
import {
  getFields,
  listFormats,
  getFormat,
  createFormat,
  updateFormat,
  deleteFormat,
  previewFormat,
  defaultRendering,
  starterColumns,
  type FieldSpec,
  type FormatColumn,
  type CsvFormat,
  type CsvFormatDraft,
} from "@/api/payoutCsvFormats";
import {
  getEligible,
  listExports,
  getExport,
  createExport,
  resolveExport,
  cancelExport,
  downloadExport,
  type ExportVariant,
  type PayoutExport,
  type ExportLine,
  type ExportResult,
} from "@/api/payoutExports";

type Tab = "files" | "layouts";

// Fallback toast body when the server sends no recognised error code. Names
// the surface so an operator knows which action failed, rather than the
// generic "something went wrong" that tells them nothing.
const FALLBACK = "The manual payout service did not accept that. Nothing was changed.";

export default function ManualPayouts() {
  const role = useAuth((s) => effectiveRole(s));
  const [tab, setTab] = useState<Tab>("files");

  // The server refuses non-superadmins outright (auth.RequireSuperadmin).
  // This is the courtesy, not the control.
  if (!isSuperadminRole(role)) {
    return (
      <div className="page">
        <EmptyState
          icon={<I.Lock />}
          title="Superadmin only"
          body="Manual payout files decide what a bank is instructed to pay. Only a platform superadmin can generate them or change their layout."
        />
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Manual payouts</h1>
          <p className="sub">
            For banks with no payout API. Payouts on a manual rail wait here
            until you put them in a file, upload it to the bank, and record what
            the bank did.
          </p>
        </div>
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          label="Manual payouts section"
          options={[
            { value: "files", label: "Payment files" },
            { value: "layouts", label: "CSV layouts" },
          ]}
        />
      </header>

      {tab === "files" ? <FilesTab /> : <LayoutsTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Payment files
// ─────────────────────────────────────────────────────────────────────────────

function FilesTab() {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const [railID, setRailID] = useState<number | null>(null);
  const [formatID, setFormatID] = useState<number | null>(null);
  const [openExport, setOpenExport] = useState<number | null>(null);

  const rails = useQuery({
    queryKey: ["admin", "rails"],
    queryFn: listAdminBankRails,
  });
  // Only manual rails can produce a file. An integrated rail's payouts are
  // dispatched over its API, and exporting them would pay everyone twice —
  // the backend refuses it, and the picker does not offer it.
  const manualRails = useMemo(
    () =>
      (rails.data?.rails ?? []).filter(
        (r) => r.provider === "manual" || r.provider === "canara_bank",
      ),
    [rails.data],
  );

  // A fixed-format rail's file layout is published by the bank, so there is
  // no layout to pick and nothing under CSV layouts applies to it.
  const selectedRail = useMemo(
    () => manualRails.find((r) => r.id === railID),
    [manualRails, railID],
  );
  const fixedFormat = selectedRail?.provider === "canara_bank";
  useEffect(() => {
    if (railID == null && manualRails.length > 0) setRailID(manualRails[0].id);
  }, [manualRails, railID]);

  const formats = useQuery({ queryKey: ["payoutCsvFormats"], queryFn: listFormats });
  useEffect(() => {
    if (formatID != null || !formats.data?.length) return;
    // Prefer a layout pinned to this rail; otherwise the first one.
    const pinned = formats.data.find((f) => f.bank_account_id === railID);
    setFormatID((pinned ?? formats.data[0]).id);
  }, [formats.data, formatID, railID]);

  const eligible = useQuery({
    queryKey: ["payoutExports", "eligible", railID],
    queryFn: () => getEligible(railID as number),
    enabled: railID != null,
    refetchInterval: 30_000,
  });

  const exports = useQuery({
    queryKey: ["payoutExports", "list", railID],
    queryFn: () => listExports(railID ? { bank_account_id: railID } : undefined),
    enabled: railID != null,
  });

  const create = useMutation({
    mutationFn: () =>
      createExport({
        bank_account_id: railID as number,
        // A fixed-format rail ignores this server-side; sending 0 keeps the
        // request shape stable rather than making the field optional.
        format_id: fixedFormat ? 0 : (formatID as number),
      }),
    onSuccess: (r) => {
      if (!r.created) {
        addToast({ title: "Nothing to export", body: r.message ?? "No payouts are waiting on this rail." });
        return;
      }
      addToast({
        title: `${r.export?.ref} created`,
        body: `${r.export?.row_count} payout(s) claimed. Download the file and upload it to the bank.`,
      });
      void qc.invalidateQueries({ queryKey: ["payoutExports"] });
      if (r.export) setOpenExport(r.export.id);
    },
    onError: (e) => addToast({ title: "That didn\u2019t work", body: apiErrorMessage(e, FALLBACK), variant: "error" }),
  });

  const download = useMutation({
    mutationFn: async ({ ex, variant }: { ex: PayoutExport; variant?: ExportVariant }) => {
      const blob = await downloadExport(ex.id, variant);
      // A fixed-format rail returns the bank's .txt, not a CSV. Saving it
      // under the wrong extension is how an operator ends up uploading a
      // file the portal will not even open.
      if (!fixedFormat) {
        saveBlob(`payouts-${ex.ref}.csv`, blob);
        return;
      }
      // The extension has to match what the server actually sent, or Excel
      // refuses to open a workbook saved as .txt. The payee download is a zip
      // when the batch has both on-us and off-us payees, since those are two
      // structurally different uploads.
      if (variant === "txt") {
        saveBlob(`payouts-${ex.ref}.txt`, blob);
      } else if (variant === "payees") {
        const zipped = blob.type === "application/zip";
        saveBlob(`payees-${ex.ref}.${zipped ? "zip" : "txt"}`, blob);
      } else {
        saveBlob(`payouts-${ex.ref}.xlsx`, blob);
      }
    },
    onError: (e) => addToast({ title: "That didn\u2019t work", body: apiErrorMessage(e, FALLBACK), variant: "error" }),
  });

  if (rails.isLoading) return <div className="card">Loading rails…</div>;

  if (manualRails.length === 0) {
    return (
      <EmptyState
        icon={<I.Banks />}
        title="No file rails yet"
        body="Add a bank rail with a no-API provider on a merchant's Bank accounts screen. You'll need the account number and IFSC the transfers are debited from — they go on every row of the file."
      />
    );
  }

  const waiting = eligible.data;

  return (
    <>
      <section className="card">
        <div className="row gap">
          <label className="field">
            <span>Rail</span>
            <select
              value={railID ?? ""}
              onChange={(e) => {
                setRailID(Number(e.target.value));
                setOpenExport(null);
              }}
            >
              {manualRails.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label} — {r.merchant_display_name || r.merchant_slug}
                </option>
              ))}
            </select>
          </label>
          {fixedFormat ? (
            <label className="field">
              <span>File layout</span>
              <div className="dim" style={{ paddingTop: 6, fontSize: 12 }}>
                Fixed by the bank — nothing to choose.
              </div>
            </label>
          ) : (
            <label className="field">
              <span>CSV layout</span>
              <select
                value={formatID ?? ""}
                onChange={(e) => setFormatID(Number(e.target.value))}
              >
                {(formats.data ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {!fixedFormat && !formats.data?.length ? (
          <p className="hint neg">
            No CSV layout defined yet. Create one under <strong>CSV layouts</strong>{" "}
            first — a file with no agreed columns is one the bank will reject.
          </p>
        ) : (
          <div className="waiting-row">
            <div>
              <div className="waiting-count">
                {waiting ? waiting.count : "—"} payout
                {waiting?.count === 1 ? "" : "s"} waiting
              </div>
              <div className="waiting-total">
                {waiting ? fmtCcyMinorExact(BigInt(waiting.total_amount_minor)) : ""}
              </div>
            </div>
            <button
              className="btn primary"
              // A fixed-format rail has no layout to select, so formatID
              // stays null forever — gating on it left the button
              // permanently dead on exactly the rails that need no choice.
              disabled={
                !waiting?.count ||
                create.isPending ||
                (!fixedFormat && formatID == null)
              }
              onClick={() => create.mutate()}
            >
              {create.isPending ? "Generating…" : "Generate payment file"}
            </button>
          </div>
        )}
        <p className="hint">
          Generating claims these payouts so no other file can include them.
          Downloading the file again afterwards is free and changes nothing.
        </p>
      </section>

      <section className="card">
        <h2>Files</h2>
        {exports.isLoading ? (
          <p>Loading…</p>
        ) : !exports.data?.length ? (
          <EmptyState title="No files yet" body="Generate one above when payouts are waiting." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Created</th>
                <th className="num">Rows</th>
                <th className="num">Total</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {exports.data.map((ex) => (
                <tr key={ex.id}>
                  <td className="mono">{ex.ref}</td>
                  <td>{new Date(ex.created_at).toLocaleString()}</td>
                  <td className="num">{ex.row_count}</td>
                  <td className="num">{fmtCcyMinorExact(BigInt(ex.total_amount_minor))}</td>
                  <td>
                    <Pill
                      tone={
                        ex.status === "settled" ? "pos" : ex.status === "cancelled" ? "" : "warn"
                      }
                    >
                      {ex.status}
                    </Pill>
                  </td>
                  <td className="right">
                    {fixedFormat ? (
                      <>
                        <button
                          className="btn"
                          title="The bank's own workbook, filled in"
                          onClick={() => download.mutate({ ex, variant: "xlsx" })}
                        >
                          Excel
                        </button>{" "}
                        <button
                          className="btn ghost"
                          title="The file the portal ingests, skipping the spreadsheet"
                          onClick={() => download.mutate({ ex, variant: "txt" })}
                        >
                          TXT
                        </button>{" "}
                        <button
                          className="btn ghost"
                          title="Register the payees. Upload this FIRST — a transfer file has nobody to pay until these are approved."
                          onClick={() => download.mutate({ ex, variant: "payees" })}
                        >
                          Beneficiaries
                        </button>{" "}
                      </>
                    ) : (
                      <>
                        <button className="btn" onClick={() => download.mutate({ ex })}>
                          Download
                        </button>{" "}
                      </>
                    )}
                    <button className="btn" onClick={() => setOpenExport(ex.id)}>
                      {ex.status === "open" ? "Record outcome" : "View"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {openExport != null && (
        <ExportDetail id={openExport} onClose={() => setOpenExport(null)} />
      )}
    </>
  );
}

// ExportDetail — the "what did the bank actually do" screen.
//
// Per-row outcomes rather than one big button, because a bank rejecting four
// rows out of two hundred is the normal case. "Mark the rest paid" exists for
// the other normal case — the file went through cleanly — but it is an
// explicit choice: an empty form must never mean "pay them all".
function ExportDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const q = useQuery({ queryKey: ["payoutExports", "detail", id], queryFn: () => getExport(id) });
  const [utrs, setUtrs] = useState<Record<number, string>>({});
  const [failed, setFailed] = useState<Record<number, string>>({});

  const resolve = useMutation({
    mutationFn: (markRest: boolean) => {
      const results: ExportResult[] = [];
      for (const [pid, reason] of Object.entries(failed)) {
        if (reason.trim()) {
          results.push({ payout_id: Number(pid), outcome: "failure", error_message: reason.trim() });
        }
      }
      for (const [pid, utr] of Object.entries(utrs)) {
        if (utr.trim() && !failed[Number(pid)]?.trim()) {
          results.push({ payout_id: Number(pid), outcome: "success", utr_no: utr.trim() });
        }
      }
      return resolveExport(id, { results, mark_remaining_success: markRest });
    },
    onSuccess: (r) => {
      addToast({
        title: "Outcomes recorded",
        body: `${r.succeeded} paid, ${r.failed} failed.`,
      });
      void qc.invalidateQueries({ queryKey: ["payoutExports"] });
    },
    onError: (e) => addToast({ title: "That didn\u2019t work", body: apiErrorMessage(e, FALLBACK), variant: "error" }),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => cancelExport(id, reason),
    onSuccess: () => {
      addToast({ title: "File cancelled", body: "Its unpaid payouts are back in the queue for the next file." });
      void qc.invalidateQueries({ queryKey: ["payoutExports"] });
      onClose();
    },
    onError: (e) => addToast({ title: "That didn\u2019t work", body: apiErrorMessage(e, FALLBACK), variant: "error" }),
  });

  if (q.isLoading || !q.data) return <section className="card">Loading file…</section>;
  const { export: ex, payouts } = q.data;
  const open = ex.status === "open";
  const stillPending = payouts.filter((p) => p.status === "pending").length;

  return (
    <section className="card">
      <div className="row between">
        <h2>
          {ex.ref} <Pill tone={open ? "warn" : "pos"}>{ex.status}</Pill>
        </h2>
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>

      {open && (
        <p className="hint">
          Upload the downloaded file to the bank, then record what came back.
          Rows you leave blank stay pending — you can come back as credits
          confirm.
        </p>
      )}

      <table className="table">
        <thead>
          <tr>
            <th>Payout</th>
            <th>Beneficiary</th>
            <th className="num">Amount</th>
            <th>Status</th>
            {open && <th>UTR (paid)</th>}
            {open && <th>Failure reason</th>}
          </tr>
        </thead>
        <tbody>
          {payouts.map((p: ExportLine) => (
            <tr key={p.payout_id}>
              <td className="mono">#{p.payout_id}</td>
              <td>
                {p.beneficiary}
                <div className="muted mono small">
                  {p.account_no} · {p.ifsc}
                </div>
              </td>
              <td className="num">{fmtCcyMinorExact(BigInt(p.amount_minor))}</td>
              <td>
                <Pill
                  tone={p.status === "success" ? "pos" : p.status === "failure" ? "neg" : "warn"}
                >
                  {p.status}
                </Pill>
                {p.utr_no && <div className="muted mono small">{p.utr_no}</div>}
                {p.error_message && <div className="muted small">{p.error_message}</div>}
              </td>
              {open && (
                <td>
                  {p.status === "pending" && (
                    <input
                      className="input small"
                      placeholder="UTR"
                      value={utrs[p.payout_id] ?? ""}
                      onChange={(e) =>
                        setUtrs((u) => ({ ...u, [p.payout_id]: e.target.value }))
                      }
                    />
                  )}
                </td>
              )}
              {open && (
                <td>
                  {p.status === "pending" && (
                    <input
                      className="input small"
                      placeholder="e.g. account closed"
                      value={failed[p.payout_id] ?? ""}
                      onChange={(e) =>
                        setFailed((f) => ({ ...f, [p.payout_id]: e.target.value }))
                      }
                    />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {open && (
        <div className="row gap end">
          <button
            className="btn"
            onClick={() => {
              const reason = window.prompt(
                "Cancel this file? Its unpaid payouts go back in the queue for the next one.\n\nWhy?",
              );
              if (reason != null) cancel.mutate(reason);
            }}
          >
            Cancel file
          </button>
          <button className="btn" disabled={resolve.isPending} onClick={() => resolve.mutate(false)}>
            Save these outcomes
          </button>
          <button
            className="btn primary"
            disabled={resolve.isPending || stillPending === 0}
            onClick={() => {
              if (
                window.confirm(
                  `Mark the remaining ${stillPending} payout(s) as PAID?\n\nOnly do this once the bank has confirmed the file went through.`,
                )
              ) {
                resolve.mutate(true);
              }
            }}
          >
            Mark the rest paid
          </button>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV layouts
// ─────────────────────────────────────────────────────────────────────────────

function LayoutsTab() {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const [editing, setEditing] = useState<number | "new" | null>(null);

  const formats = useQuery({ queryKey: ["payoutCsvFormats"], queryFn: listFormats });
  const del = useMutation({
    mutationFn: deleteFormat,
    onSuccess: () => {
      addToast({ title: "Layout deleted" });
      void qc.invalidateQueries({ queryKey: ["payoutCsvFormats"] });
    },
    onError: (e) => addToast({ title: "That didn\u2019t work", body: apiErrorMessage(e, FALLBACK), variant: "error" }),
  });

  if (editing != null) {
    return <FormatEditor id={editing} onDone={() => setEditing(null)} />;
  }

  return (
    <section className="card">
      <div className="row between">
        <h2>CSV layouts</h2>
        <button className="btn primary" onClick={() => setEditing("new")}>
          New layout
        </button>
      </div>
      <p className="hint">
        One layout per bank template. Match the bank's own column headings and
        order exactly — most upload portals reject a file whose columns they
        don't recognise.
      </p>
      {formats.isLoading ? (
        <p>Loading…</p>
      ) : !formats.data?.length ? (
        <EmptyState
          title="No layouts yet"
          body="Create one from your bank's bulk-transfer template."
        />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="num">Columns</th>
              <th>Delimiter</th>
              <th>Header row</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {formats.data.map((f) => (
              <tr key={f.id}>
                <td>
                  {f.name}
                  {f.description && <div className="muted small">{f.description}</div>}
                </td>
                <td className="num">{f.column_count ?? f.columns?.length ?? 0}</td>
                <td className="mono">{f.delimiter === "\t" ? "tab" : f.delimiter}</td>
                <td>{f.include_header ? "Yes" : "No"}</td>
                <td className="right">
                  <button className="btn" onClick={() => setEditing(f.id)}>
                    Edit
                  </button>{" "}
                  <button
                    className="btn"
                    onClick={() => {
                      if (window.confirm(`Delete "${f.name}"?`)) del.mutate(f.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const EMPTY_DRAFT: CsvFormatDraft = {
  name: "",
  description: "",
  delimiter: ",",
  include_header: true,
  bank_account_id: null,
  columns: starterColumns(),
};

function FormatEditor({ id, onDone }: { id: number | "new"; onDone: () => void }) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const [draft, setDraft] = useState<CsvFormatDraft>(EMPTY_DRAFT);
  const [preview, setPreview] = useState("");
  const [previewError, setPreviewError] = useState("");

  const fields = useQuery({ queryKey: ["payoutCsvFields"], queryFn: getFields });
  const existing = useQuery({
    queryKey: ["payoutCsvFormats", id],
    queryFn: () => getFormat(id as number),
    enabled: id !== "new",
  });

  useEffect(() => {
    if (id === "new" || !existing.data) return;
    const f: CsvFormat = existing.data;
    setDraft({
      name: f.name,
      description: f.description,
      delimiter: f.delimiter,
      include_header: f.include_header,
      bank_account_id: f.bank_account_id ?? null,
      columns: (f.columns ?? []).map((c) => ({ ...c })),
    });
  }, [existing.data, id]);

  // Live preview, rendered by the BACKEND through the same code the real
  // export uses. A preview computed here would be a preview of this file's
  // idea of the layout — which is the one thing it must not be.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      previewFormat({ ...draft, name: draft.name || "preview" })
        .then((csv) => {
          if (cancelled) return;
          setPreview(csv);
          setPreviewError("");
        })
        .catch((e) => {
          if (cancelled) return;
          setPreview("");
          setPreviewError(apiErrorMessage(e, "The layout could not be rendered."));
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [draft]);

  const save = useMutation({
    mutationFn: () => (id === "new" ? createFormat(draft).then(() => {}) : updateFormat(id, draft)),
    onSuccess: () => {
      addToast({ title: "Layout saved" });
      void qc.invalidateQueries({ queryKey: ["payoutCsvFormats"] });
      onDone();
    },
    onError: (e) => addToast({ title: "That didn\u2019t work", body: apiErrorMessage(e, FALLBACK), variant: "error" }),
  });

  const specBySource = useMemo(() => {
    const m = new Map<string, FieldSpec>();
    for (const f of fields.data ?? []) m.set(f.source, f);
    return m;
  }, [fields.data]);

  // Renumbering on every structural edit keeps positions dense and unique —
  // the backend rejects duplicates, and an admin should never have to think
  // about the numbers at all.
  const setColumns = (cols: FormatColumn[]) =>
    setDraft((d) => ({ ...d, columns: cols.map((c, i) => ({ ...c, position: i + 1 })) }));

  const move = (i: number, delta: number) => {
    const next = [...draft.columns];
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setColumns(next);
  };

  return (
    <section className="card">
      <div className="row between">
        <h2>{id === "new" ? "New CSV layout" : `Edit ${draft.name || "layout"}`}</h2>
        <button className="btn" onClick={onDone}>
          Back
        </button>
      </div>

      <div className="row gap">
        <label className="field grow">
          <span>Name</span>
          <input
            className="input"
            value={draft.name}
            placeholder="e.g. HDFC bulk transfer"
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </label>
        <label className="field">
          <span>Delimiter</span>
          <select
            value={draft.delimiter}
            onChange={(e) => setDraft((d) => ({ ...d, delimiter: e.target.value }))}
          >
            <option value=",">Comma</option>
            <option value=";">Semicolon</option>
            <option value="|">Pipe</option>
            <option value={"\t"}>Tab</option>
          </select>
        </label>
        <label className="field check">
          <input
            type="checkbox"
            checked={draft.include_header}
            onChange={(e) => setDraft((d) => ({ ...d, include_header: e.target.checked }))}
          />
          <span>Include header row</span>
        </label>
      </div>
      <label className="field">
        <span>Notes</span>
        <input
          className="input"
          value={draft.description}
          placeholder="Where this template came from, quirks the bank has"
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
        />
      </label>

      <h3>Columns</h3>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: "2.5rem" }}>#</th>
            <th>Heading in the file</th>
            <th>Field</th>
            <th>Format</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {draft.columns.map((c, i) => {
            const spec = specBySource.get(c.source);
            const renderings = spec?.renderings ?? [];
            return (
              <tr key={i}>
                <td className="mono">{i + 1}</td>
                <td>
                  <input
                    className="input small"
                    value={c.header}
                    onChange={(e) => {
                      const next = [...draft.columns];
                      next[i] = { ...c, header: e.target.value };
                      setColumns(next);
                    }}
                  />
                </td>
                <td>
                  <select
                    value={c.source}
                    onChange={(e) => {
                      const next = [...draft.columns];
                      const newSpec = specBySource.get(e.target.value);
                      next[i] = {
                        ...c,
                        source: e.target.value,
                        // Reset the rendering: one carried over from a
                        // different kind is exactly what Validate rejects.
                        rendering: defaultRendering(newSpec),
                      };
                      setColumns(next);
                    }}
                  >
                    {(fields.data ?? []).map((f) => (
                      <option key={f.source} value={f.source}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  {spec?.help && <div className="muted small">{spec.help}</div>}
                </td>
                <td>
                  {c.source === "constant" ? (
                    <input
                      className="input small"
                      placeholder="fixed text"
                      value={c.constant}
                      onChange={(e) => {
                        const next = [...draft.columns];
                        next[i] = { ...c, constant: e.target.value };
                        setColumns(next);
                      }}
                    />
                  ) : renderings.length ? (
                    <select
                      value={c.rendering || defaultRendering(spec)}
                      onChange={(e) => {
                        const next = [...draft.columns];
                        next[i] = { ...c, rendering: e.target.value };
                        setColumns(next);
                      }}
                    >
                      {renderings.map((r) => (
                        <option key={r.value} value={r.value}>
                          {renderingLabel(r.value)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="right nowrap">
                  <button className="btn tiny" onClick={() => move(i, -1)} disabled={i === 0}>
                    ↑
                  </button>{" "}
                  <button
                    className="btn tiny"
                    onClick={() => move(i, 1)}
                    disabled={i === draft.columns.length - 1}
                  >
                    ↓
                  </button>{" "}
                  <button
                    className="btn tiny"
                    onClick={() => setColumns(draft.columns.filter((_, j) => j !== i))}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        className="btn"
        onClick={() =>
          setColumns([
            ...draft.columns,
            { position: 0, header: "", source: "beneficiary_name", rendering: "", constant: "" },
          ])
        }
      >
        Add column
      </button>

      <h3>Preview</h3>
      {previewError ? (
        <p className="hint neg">{previewError}</p>
      ) : (
        <pre className="csv-preview">{preview || "…"}</pre>
      )}
      <p className="hint">
        Rendered by the server through the same code that writes the real file,
        against two sample payouts.
      </p>

      <div className="row gap end">
        <button className="btn" onClick={onDone}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={save.isPending || !!previewError || !draft.name.trim()}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save layout"}
        </button>
      </div>
    </section>
  );
}

// renderingLabel turns a backend rendering token into words. Kept here rather
// than served, because it is presentation only — the VALUES are the contract
// and those come from the server.
function renderingLabel(v: string): string {
  switch (v) {
    case "rupees_2dp":
      return "Rupees, 2 decimals (1234.50)";
    case "rupees_plain":
      return "Whole rupees — truncates paise (1234)";
    case "paise":
      return "Paise (123450)";
    case "imps_neft":
      return "IMPS / NEFT";
    case "numeric":
      return "1 / 2";
    case "lower":
      return "imps / neft";
    case "iso":
      return "2026-08-18";
    case "dd/mm/yyyy":
      return "18/08/2026";
    case "dd-mm-yyyy":
      return "18-08-2026";
    case "iso_datetime":
      return "2026-08-18T09:31:07Z";
    default:
      return v;
  }
}
